import { useState, useEffect, useCallback, useRef } from 'react';
import { api, type CatalogMeta, type CatalogPayload, type CatalogProduct, type CatalogOption } from './api';
import { useSession } from './session';
import { ClientCompanyInput } from './ClientCompanyInput';
import { ExistingDeals } from './ExistingDeals';
import { ManageUsers } from './ManageUsers';
import { repriceLines, describeChanges } from './reprice';
import { crmApi, authApi, canManageUsers } from './api';
import type { SavedClient } from './api';
import { useQuoteStore } from './store';
import { COUNTRY_OPTIONS } from './countries';
import { PROVINCE_TAX_TABLE, computeQuoteTotals, getCommitmentRate, optionNetPrice, type LineItem, type Market } from '@quote-tool/shared';

// addonFamily name for the always-included SYSTEM disk (vs the configurable DATA
// storage in family 'storage'). Both are option type 'disk'; this tells them apart.
const SYSTEM_DISK_FAMILY = 'system-storage';

const PICKER_DC_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'bhs', label: 'Beauharnois, Canada (BHS)' },
  { value: 'ca-east-tor-a', label: 'Toronto, Canada (YYZ)' },
  { value: 'sbg', label: 'Strasbourg, France (SBG)' },
  { value: 'gra', label: 'Gravelines, France (GRA)' },
  { value: 'rbx', label: 'Roubaix, France (RBX)' },
  { value: 'eu-west-par-a', label: 'Paris A, France (PAR A)' },
  { value: 'eu-west-par-b', label: 'Paris B, France (PAR B)' },
  { value: 'eu-west-par-c', label: 'Paris C, France (PAR C)' },
  { value: 'fra', label: 'Frankfurt, Germany (FRA)' },
  { value: 'lon', label: 'London, UK (LON)' },
  { value: 'waw', label: 'Warsaw, Poland (WAW)' },
  { value: 'sgp', label: 'Singapore (SGP)' },
  { value: 'syd', label: 'Sydney, Australia (SYD)' },
  { value: 'ynm', label: 'Mumbai, India (YNM)' },
];

const PICKER_FAMILY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'baremetal', label: 'BareMetal' },
  { value: 'hpc_vmware', label: 'HPC VMware' },
];

const DC_LABELS: Record<string, string> = Object.fromEntries(
  PICKER_DC_OPTIONS.map((o) => [o.value, o.label])
);

export function App() {
  const [refreshing, setRefreshing] = useState(false);

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  // URL of the most recent PREVIEW blob. Kept alive so an open preview tab stays
  // valid for the session; revoked when the next Preview/Download runs so at most
  // one preview blob is ever alive (no leak, no premature 60s revoke).
  const lastPreviewUrl = useRef<string | null>(null);
  // Quote number of the deal currently on screen. Set after the first print and
  // when an existing deal is opened; printing again reuses it instead of
  // creating a second deal for the same quote.
  const [openDealId, setOpenDealId] = useState<string | null>(null);
  const [dealsOpen, setDealsOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  // Shown after a catalog refresh re-prices an opened deal.
  const [repriceNotice, setRepriceNotice] = useState<string | null>(null);

  const [meta, setMeta] = useState<CatalogMeta | null>(null);

  const [pickerOpen, setPickerOpen] = useState(false);

  const [catalog, setCatalog] = useState<CatalogPayload | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  const [pickerDc, setPickerDc] = useState<string>('');
  const [pickerFamilies, setPickerFamilies] = useState<Set<string>>(new Set(['baremetal', 'hpc_vmware']));
  const [pickerSearch, setPickerSearch] = useState('');

  const [configProduct, setConfigProduct] = useState<CatalogProduct | null>(null);
  // Id of the line the configurator is EDITING, or null when it is adding a new
  // one. The same modal serves both; this is the only thing that differs.
  const [configEditingLineId, setConfigEditingLineId] = useState<string | null>(null);
  const [selectedRam, setSelectedRam] = useState<string>('');      // option_code
  const [selectedDisk, setSelectedDisk] = useState<string>('');    // option_code
  const [selectedBandwidth, setSelectedBandwidth] = useState<string>(''); // option_code

  const [isWideScreen, setIsWideScreen] = useState(() => window.innerWidth >= 1100);

  useEffect(() => {
    const handleResize = () => setIsWideScreen(window.innerWidth >= 1100);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [expandedLines, setExpandedLines] = useState<Set<string>>(new Set());
  // Which option's discount field is open, and its in-progress text. Held as a
  // string so a half-typed value does not round-trip through the quote.
  const [discountEdit, setDiscountEdit] =
    useState<{ lineId: string; optionId: string; value: string } | null>(null);

  const toggleLineExpanded = (lineId: string) => {
    setExpandedLines((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const lastPickerDc = useQuoteStore((s) => s.lastPickerDc);
  const setLastPickerDc = useQuoteStore((s) => s.setLastPickerDc);
  const addLine = useQuoteStore((s) => s.addLine);

  const [customOpen, setCustomOpen] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [customMonthlyPrice, setCustomMonthlyPrice] = useState<string>(''); // string for input control
  const [customSetupFee, setCustomSetupFee] = useState<string>('');
  const [customDc, setCustomDc] = useState<string>(lastPickerDc || 'bhs');
  const [customCommitMonths, setCustomCommitMonths] = useState<0 | 12 | 24 | 36>(0);
  const [customApplyPartnerDiscount, setCustomApplyPartnerDiscount] = useState<boolean>(true);
  const [customIsProfessionalServices, setCustomIsProfessionalServices] = useState<boolean>(false);
  const [customEditingLineId, setCustomEditingLineId] = useState<string | null>(null);

  const lines = useQuoteStore((s) => s.quote.lines);
  const removeLine = useQuoteStore((s) => s.removeLine);
  const updateLine = useQuoteStore((s) => s.updateLine);
  // Market selectors are read here (before the catalog-fetch effect) so the
  // effect can depend on `market` and refetch the correct DB after a switch.
  const market = useQuoteStore((s) => s.quote.market ?? 'CA');
  const setMarket = useQuoteStore((s) => s.setMarket);
  const clearLines = useQuoteStore((s) => s.clearLines);
  const loadQuote = useQuoteStore((s) => s.loadQuote);
  const setLines = useQuoteStore((s) => s.setLines);

  // Scoped to the active market so the banner's "last refreshed" always describes
  // the DB the picker is reading (catalog.db for CA, catalog-world.db for World).
  const loadMeta = useCallback(async () => {
    try {
      const m = await api.getCatalogMeta(market);
      setMeta(m);
    } catch (err) {
      console.error('Failed to load catalog meta', err);
    }
  }, [market]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  useEffect(() => {
    if (!pickerOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPickerOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [pickerOpen]);

  useEffect(() => {
    if (!pickerOpen) return;
    if (catalog !== null) return; // already loaded — use cache
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(null);
    // Pull the market-correct DB. A market switch sets catalog=null (cache
    // invalidation), so this refetches with the new market on the next open.
    api.getCatalog(market)
      .then((data) => {
        if (cancelled) return;
        setCatalog(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setCatalogError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (cancelled) return;
        setCatalogLoading(false);
      });
    return () => { cancelled = true; };
  }, [pickerOpen, catalog, market]);

  useEffect(() => {
    if (pickerOpen) {
      setPickerDc(lastPickerDc);
      setPickerSearch('');
    }
  }, [pickerOpen, lastPickerDc]);

  // Logged-in extras. Anonymous use is unchanged: `user` stays null and every
  // branch below falls back to the behaviour the tool already had.
  const session = useSession();
  const loggedIn = session.user !== null;

  const repName = useQuoteStore((s) => s.quote.repName);
  const setRepName = useQuoteStore((s) => s.setRepName);

  // Pre-fill "Your name" from the signed-in user's full name, once. Guarded on
  // the field being EMPTY and on the session having finished loading, so it can
  // never overwrite a name the rep has already typed — it is a starting value,
  // not a locked one, and stays editable.
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || session.loading) return;
    const full = session.user?.fullName?.trim();
    if (!full) return;
    if (useQuoteStore.getState().quote.repName.trim() !== '') return;
    setRepName(full);
    prefilled.current = true;
  }, [session.loading, session.user, setRepName]);

  // Fills the whole address block when a saved client is picked.
  const applySavedClient = (c: SavedClient) => {
    setClient({
      company: c.company,
      attention: c.attention,
      contactName: c.contactName,
      contactEmail: c.contactEmail,
      contactPhone: c.contactPhone,
      address: c.address,
      city: c.city,
      postalCode: c.postalCode,
      nic: c.nic,
      taxExempt: c.taxExempt,
      // Province is CA-only and country is WORLD-only; carry across whichever
      // the saved record actually has so a market switch is not undone here.
      ...(c.province ? { province: c.province as never } : {}),
      ...(c.country ? { country: c.country } : {}),
    });
  };
  const language = useQuoteStore((s) => s.quote.language);
  const setLanguage = useQuoteStore((s) => s.setLanguage);
  const comments = useQuoteStore((s) => s.quote.comments);
  const setComments = useQuoteStore((s) => s.setComments);
  const client = useQuoteStore((s) => s.quote.client);
  const setClient = useQuoteStore((s) => s.setClient);
  const partnerTier = useQuoteStore((s) => s.quote.partnerTier);
  const setPartnerTier = useQuoteStore((s) => s.setPartnerTier);
  const supportTier = useQuoteStore((s) => s.quote.supportTier);
  const setSupportTier = useQuoteStore((s) => s.setSupportTier);
  const specialDiscountPct = useQuoteStore((s) => s.quote.specialDiscountPct ?? 0);
  const setSpecialDiscountPct = useQuoteStore((s) => s.setSpecialDiscountPct);

  // Switch CA <-> World. Existing lines carry a market-specific plan code + price
  // (CA and World baremetal plans diverge by version), so they can't be repriced
  // in place — confirm, then clear them. setCatalog(null) invalidates the cached
  // catalog so the picker refetches the correct DB on next open.
  const switchMarket = (next: Market) => {
    if (next === market) return;
    if (lines.length > 0) {
      if (!window.confirm('Switching market clears the current line items — continue?')) {
        return; // abort — keep the current market and lines
      }
      clearLines();
    }
    setMarket(next);
    setCatalog(null);
    // Province is CA-only, country is WORLD-only. WORLD: clear province (else the
    // PDF address renders a bogus "City, QC ..."). CA: restore the default
    // province (controlled <select> + CA taxes need one) and clear country so it
    // never leaks onto a CA address.
    if (next === 'WORLD') setClient({ province: undefined });
    else setClient({ province: client.province ?? 'QC', country: '' });
  };

  const filteredProducts = catalog && pickerDc
    ? catalog.products
        .filter((p) => {
          if (!pickerFamilies.has(p.family)) return false;
          if (!p.datacenters.includes(pickerDc)) return false;
          if (pickerSearch.trim()) {
            const haystack = (p.name + ' ' + p.range + ' ' + p.planCode).toLowerCase();
            if (!haystack.includes(pickerSearch.trim().toLowerCase())) return false;
          }
          return true;
        })
        .sort((a, b) => a.monthlyPriceCad - b.monthlyPriceCad)
    : [];

  const handleConfigureProduct = (product: CatalogProduct) => {
    // Compute defaults: cheapest $0 option per family, or first option if no $0 exists
    const pickDefault = (type: string, extra?: (o: CatalogOption) => boolean): string => {
      const opts = product.options.filter((o) => o.type === type && (!extra || extra(o)));
      if (opts.length === 0) return '';
      // Sort by price ascending — the cheapest $0 (or cheapest period) wins
      const sorted = [...opts].sort((a, b) => a.monthlyPriceCad - b.monthlyPriceCad);
      return sorted[0]?.optionCode ?? '';
    };

    setSelectedRam(pickDefault('ram'));
    // The disk dropdown configures the DATA storage only; the included system disk
    // (family 'system-storage') is fixed and shown read-only, so exclude it here.
    setSelectedDisk(pickDefault('disk', (o) => o.family !== SYSTEM_DISK_FAMILY));
    setSelectedBandwidth(pickDefault('bandwidth'));
    setConfigEditingLineId(null);   // opening from the picker always ADDS
    setConfigProduct(product);
  };

  const handleAddCustomLine = () => {
    // Reset modal state to defaults
    setCustomName('');
    setCustomDescription('');
    setCustomMonthlyPrice('');
    setCustomSetupFee('');
    setCustomDc(lastPickerDc || 'bhs');
    setCustomCommitMonths(0);
    setCustomApplyPartnerDiscount(true);
    setCustomIsProfessionalServices(false);
    setCustomEditingLineId(null);    // ← ensure we're in add mode, not lingering edit mode
    setCustomOpen(true);
  };

  const handleEditCustomLine = (lineId: string) => {
    const line = lines.find((l) => l.id === lineId);
    if (!line || !line.isCustomLine) return;
    setCustomName(line.name);
    setCustomDescription(line.description);
    setCustomMonthlyPrice(String(line.basePriceMonthly));
    setCustomSetupFee(line.basePriceSetup > 0 ? String(line.basePriceSetup) : '');
    setCustomDc(line.datacenter);
    setCustomCommitMonths(line.commitMonths);
    setCustomApplyPartnerDiscount(line.applyPartnerDiscount);
    setCustomIsProfessionalServices(line.isProfessionalServices === true);
    setCustomEditingLineId(lineId);
    setCustomOpen(true);
  };

  const handleSubmitCustomLine = () => {
    const monthly = parseFloat(customMonthlyPrice);
    const setup = customSetupFee.trim() === '' ? 0 : parseFloat(customSetupFee);

    if (!customName.trim() || isNaN(monthly) || monthly <= 0) return;

    if (customEditingLineId) {
      // EDIT MODE — update existing line
      updateLine(customEditingLineId, {
        name: customName.trim(),
        description: customDescription.trim(),
        basePriceMonthly: monthly,
        basePriceSetup: isNaN(setup) ? 0 : setup,
        datacenter: customDc,
        commitMonths: customCommitMonths,
        applyPartnerDiscount: customApplyPartnerDiscount,
        isProfessionalServices: customIsProfessionalServices,
      });
    } else {
      // ADD MODE — create new line (existing behavior)
      const newLine: LineItem = {
        id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        family: 'baremetal',                // family doesn't matter for custom; pick a valid one for typing
        range: 'advance',                    // range doesn't matter; engine skips commitment for custom anyway
        name: customName.trim(),
        datacenter: customDc,
        releaseYear: null,                   // custom lines have no year
        description: customDescription.trim(),
        isEco: false,
        basePriceMonthly: monthly,
        basePriceSetup: isNaN(setup) ? 0 : setup,
        qty: 1,
        commitMonths: customCommitMonths,
        options: [],
        isCustomLine: true,
        applyPartnerDiscount: customApplyPartnerDiscount,
        isProfessionalServices: customIsProfessionalServices,
      };
      addLine(newLine);
    }

    setLastPickerDc(customDc);
    setCustomEditingLineId(null);    // reset edit-mode marker
    setCustomOpen(false);
  };

  const handleAddProduct = (product: CatalogProduct) => {
    if (!pickerDc) return;

    const newLine = {
      id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      family: product.family as 'baremetal' | 'hpc_vmware',
      range: product.range as never,
      planCode: product.planCode,
      name: product.name,
      datacenter: pickerDc,
      releaseYear: product.releaseYear,
      description: product.descriptionEn ?? product.name,
      isEco: product.isEco,
      basePriceMonthly: product.monthlyPriceCad,
      basePriceSetup: product.setupFeeCad,
      qty: 1,
      commitMonths: 0 as const,
      options: [],
      isCustomLine: false,
      applyPartnerDiscount: false,    // catalog lines: partner discount handled by isPartnerDiscountEligible
    };
    addLine(newLine);
    setLastPickerDc(pickerDc);
    setPickerOpen(false);
  };

  /**
   * Reopen the configurator on an existing catalog line.
   *
   * The line stores its planCode, so the product is looked up in the catalog
   * that is already loaded. The current selections are restored by matching the
   * line's saved options back to the product's option codes; anything the line
   * does not carry (a $0 included option, which is never stored) falls back to
   * the same cheapest-first default the picker uses.
   */
  /**
   * Whether reopening the configurator on this line would show anything.
   *
   * A line that already carries priced options clearly qualifies. One that does
   * not may still be configurable — its options could all have been the $0
   * included defaults — so the catalog is consulted when it is loaded. With no
   * catalog in memory yet, fall back to "yes" for BareMetal, which is the only
   * family the configurator handles: showing a pencil that opens an empty modal
   * is a smaller sin than hiding one the rep needs.
   */
  const lineHasEditableOptions = (line: LineItem): boolean => {
    if (line.isCustomLine) return false;
    if (line.options.length > 0) return true;
    if (!catalog) return line.family === 'baremetal';
    const product =
      (line.planCode && catalog.products.find((p) => p.planCode === line.planCode)) ||
      catalog.products.find((p) => p.name === line.name);
    return !!product && product.options.some((o) => o.type === 'ram' || o.type === 'disk' || o.type === 'bandwidth');
  };

  const handleEditConfiguredLine = async (lineId: string) => {
    const line = lines.find((l) => l.id === lineId);
    if (!line || line.isCustomLine) return;

    // The catalog may not be loaded yet if the rep never opened the picker.
    let cat = catalog;
    if (!cat) {
      try {
        cat = await api.getCatalog(market);
        setCatalog(cat);
      } catch (err) {
        setPdfError(err instanceof Error ? err.message : String(err));
        return;
      }
    }

    const product =
      (line.planCode && cat.products.find((p) => p.planCode === line.planCode)) ||
      cat.products.find((p) => p.name === line.name);
    if (!product) {
      // Pre-planCode quotes, or a product withdrawn from the catalog.
      setPdfError(
        `"${line.name}" is no longer in the catalog, so its options cannot be edited. ` +
        'Remove the line and add it again.',
      );
      return;
    }

    const codeFor = (type: string, extra?: (o: CatalogOption) => boolean): string => {
      const chosen = line.options.find((o) =>
        product.options.some(
          (po) => po.optionCode === o.id && po.type === type && (!extra || extra(po)),
        ),
      );
      if (chosen) return chosen.id;
      const opts = product.options.filter((o) => o.type === type && (!extra || extra(o)));
      const sorted = [...opts].sort((a, b) => a.monthlyPriceCad - b.monthlyPriceCad);
      return sorted[0]?.optionCode ?? '';
    };

    setSelectedRam(codeFor('ram'));
    setSelectedDisk(codeFor('disk', (o) => o.family !== SYSTEM_DISK_FAMILY));
    setSelectedBandwidth(codeFor('bandwidth'));
    setPickerDc(line.datacenter);
    setConfigEditingLineId(lineId);
    setConfigProduct(product);
    setPickerOpen(true);
  };

  /** Apply the configurator's current selections to the line being edited. */
  const handleSaveConfiguredLine = () => {
    if (!configProduct || !configEditingLineId) return;

    const optionFromCode = (code: string) => {
      const opt = configProduct.options.find((o) => o.optionCode === code);
      // $0 options are "included" — the base price already covers them, so they
      // are never stored as separate priced options.
      if (!opt || opt.monthlyPriceCad === 0) return null;
      return {
        id: opt.optionCode,
        name: opt.name,
        monthlyPrice: opt.monthlyPriceCad,
        appliesCommitmentDiscount: opt.appliesCommitmentDiscount,
      };
    };

    const lineOptions = [selectedRam, selectedDisk, selectedBandwidth]
      .map(optionFromCode)
      .filter((o) => o !== null);

    // The base price is refreshed from the catalog too: the rep may be editing a
    // line that was added before a catalog refresh, and leaving a stale base
    // while updating the options would produce a total that matches neither.
    updateLine(configEditingLineId, {
      planCode: configProduct.planCode,
      basePriceMonthly: configProduct.monthlyPriceCad,
      basePriceSetup: configProduct.setupFeeCad,
      options: lineOptions,
    });

    setConfigEditingLineId(null);
    setConfigProduct(null);
    setPickerOpen(false);
  };

  const handleAddConfiguredProduct = () => {
    if (!configProduct || !pickerDc) return;

    // Build the options array. Skip $0 "included" options — base price already covers them.
    const optionFromCode = (code: string) => {
      const opt = configProduct.options.find((o) => o.optionCode === code);
      if (!opt || opt.monthlyPriceCad === 0) return null;
      return {
        id: opt.optionCode,
        name: opt.name,
        monthlyPrice: opt.monthlyPriceCad,
        appliesCommitmentDiscount: opt.appliesCommitmentDiscount,
      };
    };

    const ramOpt = optionFromCode(selectedRam);
    const diskOpt = optionFromCode(selectedDisk);
    const bwOpt = optionFromCode(selectedBandwidth);

    const lineOptions = [ramOpt, diskOpt, bwOpt].filter((o) => o !== null);

    const newLine = {
      id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      family: configProduct.family as 'baremetal' | 'hpc_vmware',
      range: configProduct.range as never,
      planCode: configProduct.planCode,
      name: configProduct.name,
      datacenter: pickerDc,
      releaseYear: configProduct.releaseYear,
      description: configProduct.descriptionEn ?? configProduct.name,
      isEco: configProduct.isEco,
      basePriceMonthly: configProduct.monthlyPriceCad,
      basePriceSetup: configProduct.setupFeeCad,
      qty: 1,
      commitMonths: 0 as const,
      options: lineOptions,
      isCustomLine: false,
      applyPartnerDiscount: false,    // catalog lines: partner discount handled by isPartnerDiscountEligible
    };

    addLine(newLine);
    setLastPickerDc(pickerDc);
    setConfigProduct(null);    // reset configurator state
    setPickerOpen(false);       // close the modal
  };

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      // No market argument -> refreshes CA and World together, so neither market
      // is left quoting yesterday's option prices.
      const result = await api.refreshCatalog();
      for (const m of result.markets ?? []) {
        console.log(
          `Catalog refresh OK [${m.market}] — ${m.priceDiff.productsChanged} product ` +
          `and ${m.priceDiff.optionsChanged} option prices changed`,
          m.priceDiff.samples,
        );
      }
      if (result.failures?.length) {
        console.error('Catalog refresh failed for some markets', result.failures);
      }
      // The in-memory catalog now holds pre-refresh prices — drop it so the next
      // picker open refetches the freshly written rows.
      setCatalog(null);
      await loadMeta();

      // An opened deal has to be re-priced against what was just written, or a
      // reprint would go out carrying figures the catalog no longer backs.
      const current = useQuoteStore.getState().quote;
      if (openDealId && current.lines.length > 0) {
        const fresh = await api.getCatalog(current.market ?? 'CA');
        setCatalog(fresh);
        const repriced = repriceLines(current.lines, fresh);
        if (repriced.changes.length > 0) setLines(repriced.lines);
        setRepriceNotice(describeChanges(repriced));
      }
    } catch (err) {
      console.error('Catalog refresh failed', err);
    } finally {
      setRefreshing(false);
    }
  };

  /** Load a saved deal into the form, keeping its quote number for reprints. */
  const handleOpenDeal = async (dealId: string) => {
    try {
      const { deal } = await crmApi.getDeal(dealId);
      loadQuote(deal.quote);
      // Holding the id is what makes the next Preview/Download reuse this
      // quote number instead of minting a second one for the same quote.
      setOpenDealId(deal.id);
      // The stored quote may be for the other market; invalidate the cached
      // catalog so the picker refetches the right one.
      setCatalog(null);
      setRepriceNotice(null);
      setDealsOpen(false);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : String(err));
    }
  };

  /** Only a presales profile may author a discount. Server-enforced too. */
  const isPresales = session.user?.isPresales === true;

  const discountBtn = (primary: boolean): React.CSSProperties => ({
    padding: '2px 8px', fontSize: '11px', fontWeight: 500, borderRadius: '4px',
    cursor: 'pointer', fontFamily: 'inherit',
    background: primary ? '#000E9C' : 'white',
    color: primary ? 'white' : '#000E9C',
    border: primary ? 'none' : '1px solid #C7D2FE',
  });

  /**
   * Write the edited discount onto its option.
   *
   * Clamped to 0-100 here as well as in the engine: the field is a number input
   * the rep can type anything into, and a rate outside that range would either
   * raise the price or drive it negative.
   */
  const commitDiscount = () => {
    if (!discountEdit) return;
    const raw = discountEdit.value.trim();
    const parsed = raw === '' ? 0 : Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) {
      setPdfError('Discount must be a percentage between 0 and 100.');
      return;
    }
    const line = lines.find((l) => l.id === discountEdit.lineId);
    if (!line) { setDiscountEdit(null); return; }
    updateLine(line.id, {
      options: line.options.map((o) =>
        o.id === discountEdit.optionId ? { ...o, customDiscountPct: parsed } : o,
      ),
    });
    setPdfError(null);
    setDiscountEdit(null);
  };

  // ---- PDF generation (Preview / Download) ----
  // Strip characters illegal in filenames; never produce an empty name.
  const sanitize = (s: string) =>
    (s || 'quote').replace(/[/\\:*?"<>|]/g, '').trim() || 'quote';

  const runPdf = async (consume: (url: string) => void) => {
    if (pdfLoading) return;
    // A new generate (Preview OR Download) supersedes any prior preview: revoke
    // the previous preview blob first so they never accumulate.
    if (lastPreviewUrl.current) {
      URL.revokeObjectURL(lastPreviewUrl.current);
      lastPreviewUrl.current = null;
    }
    setPdfLoading(true);
    setPdfError(null);
    try {
      const quote = useQuoteStore.getState().quote;
      // Reprinting an opened deal reuses its number so the customer's copy and
      // the stored deal keep matching; a fresh quote passes undefined and the
      // backend mints one.
      const { blob, quoteNumber } = await api.generateQuotePdf(quote, openDealId ?? undefined);
      if (quoteNumber) setOpenDealId(quoteNumber);
      const url = URL.createObjectURL(blob);
      consume(url);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : String(err));
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePreview = () =>
    runPdf((url) => {
      window.open(url, '_blank');
      // No timed revoke: keep the blob alive so the opened tab stays valid for the
      // session. The next Preview/Download revokes it (see runPdf).
      lastPreviewUrl.current = url;
    });

  const handleDownload = () =>
    runPdf((url) => {
      const company = useQuoteStore.getState().quote.client.company;
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitize(company)} - quote.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    });

  const formatRelative = (iso: string | null): string => {
    if (!iso) return 'never';
    const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} minute${Math.floor(diffSec / 60) === 1 ? '' : 's'} ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hour${Math.floor(diffSec / 3600) === 1 ? '' : 's'} ago`;
    const days = Math.floor(diffSec / 86400);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  };

  const getProvinceTaxesForDisplay = (): Array<{ name: string; rate: number }> => {
    // Adapted to the actual shared shape: PROVINCE_TAX_TABLE keyed by province code,
    // with each rule exposing gst/hst rates plus a combined pstQst rate + pstQstLabel
    // (e.g. "QST 9.975%"). We flatten to [{name, rate}] for the named taxes that apply.
    try {
      // WORLD quotes carry no province (optional in the shared type) — no CA tax rows.
      if (!client.province) return [];
      const rule = PROVINCE_TAX_TABLE[client.province];
      if (!rule) return [];
      const rows: Array<{ name: string; rate: number }> = [];
      if (rule.hst > 0) rows.push({ name: 'HST', rate: rule.hst });
      if (rule.gst > 0) rows.push({ name: 'GST', rate: rule.gst });
      if (rule.pstQst > 0) {
        // pstQstLabel is like "QST 9.975%" / "PST 7%" / "RST 7%" — take the leading name token.
        const name = rule.pstQstLabel.split(' ')[0] || 'PST';
        rows.push({ name, rate: rule.pstQst });
      }
      return rows;
    } catch {
      return [];
    }
  };

  const computeLinePerUnitMonthly = (line: typeof lines[number]) => {
    const optionsSum = line.options.reduce((sum, o) => sum + optionNetPrice(o), 0);
    return line.basePriceMonthly + optionsSum;
  };

  const getOptionByCode = (product: CatalogProduct, code: string): CatalogOption | undefined => {
    return product.options.find((o) => o.optionCode === code);
  };

  const configLiveTotal = (() => {
    if (!configProduct) return { base: 0, ramDelta: 0, diskDelta: 0, bwDelta: 0, total: 0 };
    const ram = getOptionByCode(configProduct, selectedRam);
    const disk = getOptionByCode(configProduct, selectedDisk);
    const bw = getOptionByCode(configProduct, selectedBandwidth);
    const base = configProduct.monthlyPriceCad;
    const ramDelta = ram ? ram.monthlyPriceCad : 0;
    const diskDelta = disk ? disk.monthlyPriceCad : 0;
    const bwDelta = bw ? bw.monthlyPriceCad : 0;
    return {
      base,
      ramDelta,
      diskDelta,
      bwDelta,
      total: base + ramDelta + diskDelta + bwDelta,
    };
  })();

  const renderOptionDropdown = (
    label: string,
    optionType: string,
    selected: string,
    setSelected: (v: string) => void,
    familyFilter?: (o: CatalogOption) => boolean
  ) => {
    if (!configProduct) return null;
    const opts = configProduct.options
      .filter((o) => o.type === optionType && (!familyFilter || familyFilter(o)))
      .sort((a, b) => a.monthlyPriceCad - b.monthlyPriceCad);
    if (opts.length === 0) return null;

    const included = opts.filter((o) => o.monthlyPriceCad === 0);
    const paid = opts.filter((o) => o.monthlyPriceCad > 0);

    const formatPrice = (p: number) =>
      p === 0
        ? '— Included'
        : '+ $' + p.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '/mo';

    return (
      <div style={{ marginBottom: '16px' }}>
        <label style={{
          display: 'block',
          fontSize: '11px',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: '#6B6B6B',
          marginBottom: '6px',
        }}>
          {label}
        </label>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          style={{
            width: '100%',
            padding: '8px 12px',
            fontSize: '14px',
            border: '1px solid #E5E7EB',
            borderRadius: '6px',
            outline: 'none',
            fontFamily: 'inherit',
            background: 'white',
            color: '#1A1A1A',
            boxSizing: 'border-box',
            cursor: 'pointer',
          }}
        >
          {included.length > 0 && (
            <optgroup label="Included (no extra cost)">
              {included.map((o) => (
                <option key={o.optionCode} value={o.optionCode}>
                  {o.name} {formatPrice(o.monthlyPriceCad)}
                </option>
              ))}
            </optgroup>
          )}
          {paid.length > 0 && (
            <optgroup label="Paid upgrades">
              {paid.map((o) => (
                <option key={o.optionCode} value={o.optionCode}>
                  {o.name} {formatPrice(o.monthlyPriceCad)}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </div>
    );
  };

  const totals = computeQuoteTotals({
    repName,
    language,
    client,
    partnerTier,
    supportTier,
    specialDiscountPct,
    lines,
    comments: '',     // comes from Section 5 later; not used in totals math
  });

  // Aggregate per-line discount sums for the audit cascade (list price → discounts → subtotal).
  const totalsAudit = (() => {
    let listPrice = 0;
    let partnerSavings = 0;

    // Group commitment savings by commitMonths.
    // For each group, also collect the set of rates seen (since different ranges at the same term can have different rates).
    const commitGroups = new Map<number, { amount: number; rates: Set<number> }>();

    for (const lc of totals.lines) {
      listPrice += lc.baseMonthlyBeforeDiscounts;
      partnerSavings += lc.partnerDiscountAmount;

      // Look up the corresponding LineItem to get commitMonths and range
      const line = lines.find((l) => l.id === lc.lineId);
      if (!line) continue;
      if (lc.commitmentDiscountAmount <= 0) continue;

      const rate = getCommitmentRate(line.family, line.range, line.commitMonths);
      const existing = commitGroups.get(line.commitMonths);
      if (existing) {
        existing.amount += lc.commitmentDiscountAmount;
        existing.rates.add(rate);
      } else {
        commitGroups.set(line.commitMonths, {
          amount: lc.commitmentDiscountAmount,
          rates: new Set([rate]),
        });
      }
    }

    // Convert the map into a sorted array (ascending by term)
    const commitmentRows = Array.from(commitGroups.entries())
      .map(([months, group]) => {
        const ratesArr = Array.from(group.rates).sort((a, b) => a - b);
        const lo = ratesArr[0] ?? 0;
        const hi = ratesArr[ratesArr.length - 1] ?? 0;
        const ratePctRange = ratesArr.length === 1
          ? `${(lo * 100).toFixed(0)}%`
          : `${(lo * 100).toFixed(0)}–${(hi * 100).toFixed(0)}%`;
        return { months, amount: group.amount, rateLabel: ratePctRange };
      })
      .sort((a, b) => a.months - b.months);

    return { listPrice, partnerSavings, commitmentRows };
  })();

  const renderYearChip = (releaseYear: number | null) => {
    if (releaseYear == null) return null;
    const isCurrent = releaseYear >= 2026;
    return (
      <span
        title={isCurrent ? `${releaseYear} — current generation` : `${releaseYear} — previous generation`}
        style={{
          display: 'inline-block',
          padding: '1px 6px',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.02em',
          borderRadius: '3px',
          background: isCurrent ? '#000E9C' : '#E5E7EB',
          color: isCurrent ? 'white' : '#6B6B6B',
          verticalAlign: 'middle',
          marginLeft: '8px',
          cursor: 'help',
        }}
      >
        {releaseYear}
      </span>
    );
  };

  return (
    <>
    <header
      className="bg-ovh text-white"
      style={{ backgroundColor: '#000E9C', color: '#FFFFFF' }}
    >
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ width: '36px', height: '36px', background: 'white', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#000E9C', fontWeight: 600, fontSize: '13px' }}>OVH</span>
          </div>
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: '16px', fontWeight: 500 }}>Quote Tool</div>
            <div style={{ fontSize: '11px', color: '#B5D4F4' }}>Canada · Partner Sales</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Signed-in identity + sign out. There is deliberately no sign-IN
            button here: /login is reached by URL. But an authenticated rep
            needs a way back out, and without this there is none. */}
        {loggedIn && (
          <span style={{ fontSize: '12px', color: '#B5D4F4', marginRight: '4px' }}>
            {session.user?.fullName || session.user?.username}
            {' · '}
            <button
              type="button"
              onClick={async () => {
                try { await authApi.logout(); } finally { window.location.assign('/'); }
              }}
              style={{
                background: 'none', border: 'none', padding: 0, font: 'inherit',
                color: 'white', cursor: 'pointer', textDecoration: 'underline',
              }}
            >
              Sign out
            </button>
          </span>
        )}
        {loggedIn && (
          <button
            onClick={() => setDealsOpen(true)}
            style={{
              fontSize: '12px',
              padding: '6px 12px',
              borderRadius: '6px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'white',
              cursor: 'pointer',
            }}
          >
            Existing deals
          </button>
        )}
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          style={{
            fontSize: '12px',
            padding: '6px 12px',
            borderRadius: '6px',
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: 'white',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            opacity: refreshing ? 0.7 : 1,
          }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh catalog'}
        </button>
        {/* Right of Refresh catalog, and only for an account that may actually
            use it. The server re-checks on every /api/users request — hiding a
            button is never the access control. */}
        {canManageUsers(session.user) && (
          <button
            onClick={() => setUsersOpen(true)}
            style={{
              fontSize: '12px',
              padding: '6px 12px',
              borderRadius: '6px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'white',
              cursor: 'pointer',
            }}
          >
            Manage Users
          </button>
        )}
        </div>
      </div>
    </header>
    <div style={{
      background: '#F5F8FF',
      borderBottom: '1px solid #E5E7EB',
      color: '#042C53',
      fontSize: '12px',
    }}>
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '8px 24px',
      }}>
        {meta === null ? (
          <span>Loading catalog status…</span>
        ) : meta.lastRefreshedAt === null ? (
          <span>Catalog not refreshed yet — click "Refresh catalog" above to load OVH data</span>
        ) : (
          <span>
            Catalog last refreshed <strong>{formatRelative(meta.lastRefreshedAt)}</strong>
            {' · '}{meta.productsCount.toLocaleString()} products
            {' · '}{meta.uniqueOptionsCount.toLocaleString()} options
          </span>
        )}
        {meta?.lastError && (
          <div style={{ color: '#B00020', marginTop: '2px' }}>
            Last error: {meta.lastError}
          </div>
        )}
      </div>
    </div>
    <main style={{
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '24px',
      display: 'grid',
      gridTemplateColumns: isWideScreen ? 'minmax(0, 1fr) 340px' : 'minmax(0, 1fr)',
      gap: '20px',
    }}>
      <div style={{ minWidth: 0, maxWidth: '720px' }}>
      <section style={{
        background: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
        }}>
          <span style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: '#E6F1FB',
            color: '#0C447C',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 600,
            flexShrink: 0,
          }}>1</span>
          <h2 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: 600,
            color: '#1A1A1A',
          }}>Quote details</h2>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          maxWidth: '720px',
        }}>
          <div>
            <label
              htmlFor="repName"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Your name
            </label>
            <input
              id="repName"
              type="text"
              value={repName}
              onChange={(e) => setRepName(e.target.value)}
              placeholder="e.g. Marie-Claude Tremblay"
              autoComplete="name"
              maxLength={200}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label
              htmlFor="language"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              PDF language
            </label>
            <select
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value as 'en' | 'fr')}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
                cursor: 'pointer',
              }}
            >
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="market"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Market
            </label>
            <select
              id="market"
              value={market}
              onChange={(e) => switchMarket(e.target.value as Market)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
                cursor: 'pointer',
              }}
            >
              <option value="CA">Canada (CAD)</option>
              <option value="WORLD">World / International (USD)</option>
            </select>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          maxWidth: '720px',
          marginTop: '20px',
        }}>
          <div>
            <label
              htmlFor="company"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Client company
            </label>
            {/* Logged in, this is a search over saved clients; logged out it
                renders the same plain input it always was. */}
            <ClientCompanyInput
              id="company"
              value={client.company}
              onChange={(company) => setClient({ company })}
              onSelect={applySavedClient}
              enabled={loggedIn}
              inputStyle={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label
              htmlFor="nic"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              NIC reference{' '}
              <span style={{
                color: '#9CA3AF',
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
              }}>(optional)</span>
            </label>
            <input
              id="nic"
              type="text"
              value={client.nic}
              onChange={(e) => setClient({ nic: e.target.value })}
              placeholder="e.g. ab12345-ovh"
              autoComplete="off"
              maxLength={50}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          maxWidth: '720px',
          marginTop: '20px',
        }}>
          <div>
            <label
              htmlFor="attention"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Attention{' '}
              <span style={{
                color: '#9CA3AF',
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
              }}>(optional)</span>
            </label>
            <input
              id="attention"
              type="text"
              value={client.attention}
              onChange={(e) => setClient({ attention: e.target.value })}
              placeholder="e.g. Procurement Department"
              autoComplete="off"
              maxLength={200}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label
              htmlFor="contactName"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Contact name
            </label>
            <input
              id="contactName"
              type="text"
              value={client.contactName}
              onChange={(e) => setClient({ contactName: e.target.value })}
              placeholder="e.g. Jean Tremblay"
              autoComplete="name"
              maxLength={200}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          maxWidth: '720px',
          marginTop: '20px',
        }}>
          <div>
            <label
              htmlFor="contactEmail"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Contact email
            </label>
            <input
              id="contactEmail"
              type="email"
              value={client.contactEmail}
              onChange={(e) => setClient({ contactEmail: e.target.value })}
              placeholder="e.g. jean.tremblay@acme.ca"
              autoComplete="email"
              maxLength={200}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label
              htmlFor="contactPhone"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Contact phone{' '}
              <span style={{
                color: '#9CA3AF',
                fontWeight: 400,
                textTransform: 'none',
                letterSpacing: 0,
              }}>(optional)</span>
            </label>
            <input
              id="contactPhone"
              type="tel"
              value={client.contactPhone}
              onChange={(e) => setClient({ contactPhone: e.target.value })}
              placeholder="e.g. +1 514 555 0123"
              autoComplete="tel"
              maxLength={50}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <div style={{ maxWidth: '720px', marginTop: '20px' }}>
          <label
            htmlFor="address"
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#6B6B6B',
              marginBottom: '6px',
            }}
          >
            Street address
          </label>
          <input
            id="address"
            type="text"
            value={client.address}
            onChange={(e) => setClient({ address: e.target.value })}
            placeholder="e.g. 1234 rue Sainte-Catherine Ouest, Suite 800"
            autoComplete="street-address"
            maxLength={300}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '14px',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              outline: 'none',
              fontFamily: 'inherit',
              background: 'white',
              color: '#1A1A1A',
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(200px, 2fr) minmax(160px, 1fr) minmax(140px, 1fr)',
          gap: '20px',
          maxWidth: '720px',
          marginTop: '20px',
        }}>
          <div>
            <label
              htmlFor="city"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              City
            </label>
            <input
              id="city"
              type="text"
              value={client.city}
              onChange={(e) => setClient({ city: e.target.value })}
              placeholder="e.g. Montréal"
              autoComplete="address-level2"
              maxLength={100}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {market === 'CA' && (
          <div>
            <label
              htmlFor="province"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Province
            </label>
            <select
              id="province"
              value={client.province}
              onChange={(e) => setClient({ province: e.target.value as typeof client.province })}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
                cursor: 'pointer',
              }}
            >
              <option value="AB">Alberta</option>
              <option value="BC">British Columbia</option>
              <option value="MB">Manitoba</option>
              <option value="NB">New Brunswick</option>
              <option value="NL">Newfoundland and Labrador</option>
              <option value="NS">Nova Scotia</option>
              <option value="NT">Northwest Territories</option>
              <option value="NU">Nunavut</option>
              <option value="ON">Ontario</option>
              <option value="PE">Prince Edward Island</option>
              <option value="QC">Quebec</option>
              <option value="SK">Saskatchewan</option>
              <option value="YT">Yukon</option>
            </select>
          </div>
          )}

          {market === 'WORLD' && (
          <div>
            <label
              htmlFor="country"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Country
            </label>
            <select
              id="country"
              value={client.country ?? ''}
              onChange={(e) => setClient({ country: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
                cursor: 'pointer',
              }}
            >
              <option value="">Select country…</option>
              {COUNTRY_OPTIONS.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          )}

          <div>
            <label
              htmlFor="postalCode"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Postal code
            </label>
            <input
              id="postalCode"
              type="text"
              value={client.postalCode}
              onChange={(e) => setClient({ postalCode: e.target.value.toUpperCase() })}
              placeholder="H3A 1B2"
              autoComplete="postal-code"
              maxLength={10}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
                textTransform: 'uppercase',
              }}
            />
          </div>
        </div>

        <div style={{ marginTop: '20px', maxWidth: '720px' }}>
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            cursor: 'pointer',
            userSelect: 'none',
          }}>
            <input
              type="checkbox"
              checked={client.taxExempt}
              onChange={(e) => setClient({ taxExempt: e.target.checked })}
              style={{
                width: '16px',
                height: '16px',
                marginTop: '2px',
                cursor: 'pointer',
                accentColor: '#000E9C',
                flexShrink: 0,
              }}
            />
            <span>
              <span style={{
                display: 'block',
                fontSize: '14px',
                color: '#1A1A1A',
                fontWeight: 500,
              }}>
                Tax-exempt
              </span>
            </span>
          </label>
        </div>
      </section>

      <section style={{
        background: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        marginTop: '20px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
        }}>
          <span style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: '#E6F1FB',
            color: '#0C447C',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 600,
            flexShrink: 0,
          }}>2</span>
          <h2 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: 600,
            color: '#1A1A1A',
          }}>Partner & Support</h2>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '20px',
          maxWidth: '720px',
        }}>
          <div>
            <label
              htmlFor="partnerTier"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Partner tier
            </label>
            <select
              id="partnerTier"
              value={partnerTier}
              onChange={(e) => setPartnerTier(e.target.value as typeof partnerTier)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
                cursor: 'pointer',
              }}
            >
              <option value="none">None — direct customer</option>
              <option value="standard">Standard (5% deal registration)</option>
              <option value="advanced">Advanced (5% deal registration + 5% profit incentives)</option>
              <option value="elite">Elite (5% deal registration + 10% profit incentives)</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="supportTier"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Support tier
            </label>
            <select
              id="supportTier"
              value={supportTier}
              onChange={(e) => setSupportTier(e.target.value as typeof supportTier)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: '14px',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                outline: 'none',
                fontFamily: 'inherit',
                background: 'white',
                color: '#1A1A1A',
                boxSizing: 'border-box',
                cursor: 'pointer',
              }}
            >
              <option value="standard">Standard — no surcharge</option>
              <option value="business">
                Business (+10%, ${market === 'WORLD' ? '300' : '400'}/mo minimum)
              </option>
              <option value="business_baremetal">Business BareMetal (+5%, no minimum)</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="specialDiscount"
              style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#6B6B6B',
                marginBottom: '6px',
              }}
            >
              Special discount
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="specialDiscount"
                type="number"
                min={0}
                max={100}
                step={0.5}
                inputMode="decimal"
                value={specialDiscountPct === 0 ? '' : specialDiscountPct}
                placeholder="0"
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw.trim() === '') { setSpecialDiscountPct(0); return; }
                  const n = parseFloat(raw);
                  if (isNaN(n)) return;
                  // Clamp to [0, 100] — mirrors the engine so the on-screen totals
                  // and the PDF always agree.
                  setSpecialDiscountPct(Math.min(100, Math.max(0, n)));
                }}
                style={{
                  width: '100%',
                  padding: '8px 28px 8px 12px',
                  fontSize: '14px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  outline: 'none',
                  fontFamily: 'inherit',
                  background: 'white',
                  color: '#1A1A1A',
                  boxSizing: 'border-box',
                }}
              />
              <span style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '14px',
                color: '#6B6B6B',
                pointerEvents: 'none',
              }}>%</span>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: '11px', color: '#9AA0A6', lineHeight: 1.4 }}>
              Exceptional discount off the monthly subtotal. Support &amp; tax are
              calculated on the reduced amount.
            </p>
          </div>
        </div>
      </section>

      <section style={{
        background: 'white',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        marginTop: '20px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
        }}>
          <span style={{
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            background: '#E6F1FB',
            color: '#0C447C',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 600,
            flexShrink: 0,
          }}>3</span>
          <h2 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: 600,
            color: '#1A1A1A',
          }}>Products & services</h2>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 500,
              background: '#000E9C',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + Add product
          </button>

          <button
            type="button"
            onClick={handleAddCustomLine}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: 500,
              background: 'white',
              color: '#000E9C',
              border: '1px solid #000E9C',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            + Custom line
          </button>
        </div>

        {lines.length === 0 ? (
          <p style={{
            marginTop: '16px',
            marginBottom: 0,
            fontSize: '13px',
            color: '#6B6B6B',
            fontStyle: 'italic',
          }}>
            No products added yet. Click "Add product" to begin.
          </p>
        ) : (
          <div style={{
            marginTop: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            {lines.map((line) => (
              <div
                key={line.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  background: 'white',
                  overflow: 'hidden',
                }}
              >
                {/* Main row */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                }}>
                {/* Leading DC chip */}
                <div
                  title={DC_LABELS[line.datacenter] ?? line.datacenter}
                  style={{
                    flexShrink: 0,
                    padding: '4px 10px',
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    background: '#E6F1FB',
                    color: '#0C447C',
                    borderRadius: '4px',
                    textTransform: 'uppercase',
                    minWidth: '64px',
                    textAlign: 'center',
                    cursor: 'help',
                  }}
                >
                  {line.datacenter}
                </div>

                {/* Product name + family·range */}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#1A1A1A',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {line.name}
                    {renderYearChip(line.releaseYear)}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#6B6B6B',
                    marginTop: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap',
                  }}>
                    <span style={line.isCustomLine ? { fontStyle: 'italic', color: '#0C447C', fontWeight: 500 } : undefined}>
                      {line.isCustomLine
                        ? 'Custom'
                        : `${line.family === 'baremetal' ? 'BareMetal' : 'HPC VMware'} · ${line.range}`}
                    </span>
                    {line.options.length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleLineExpanded(line.id)}
                        style={{
                          padding: '2px 8px',
                          fontSize: '11px',
                          fontWeight: 500,
                          background: expandedLines.has(line.id) ? '#000E9C' : '#E6F1FB',
                          color: expandedLines.has(line.id) ? 'white' : '#0C447C',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        +{line.options.length} option{line.options.length === 1 ? '' : 's'}
                        <span style={{ fontSize: '9px' }}>
                          {expandedLines.has(line.id) ? '▲' : '▼'}
                        </span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Quantity stepper */}
                <div style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                  <button
                    type="button"
                    aria-label="Decrease quantity"
                    onClick={() => {
                      if (line.qty > 1) updateLine(line.id, { qty: line.qty - 1 });
                    }}
                    disabled={line.qty <= 1}
                    style={{
                      width: '24px',
                      height: '24px',
                      padding: 0,
                      fontSize: '14px',
                      lineHeight: 1,
                      background: line.qty <= 1 ? '#F3F4F6' : 'white',
                      color: line.qty <= 1 ? '#D1D5DB' : '#1A1A1A',
                      border: '1px solid #E5E7EB',
                      borderRadius: '4px',
                      cursor: line.qty <= 1 ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                      fontWeight: 500,
                    }}
                  >
                    −
                  </button>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#1A1A1A',
                    minWidth: '24px',
                    textAlign: 'center',
                  }}>
                    {line.qty}
                  </span>
                  <button
                    type="button"
                    aria-label="Increase quantity"
                    onClick={() => {
                      if (line.qty < 99) updateLine(line.id, { qty: line.qty + 1 });
                    }}
                    disabled={line.qty >= 99}
                    style={{
                      width: '24px',
                      height: '24px',
                      padding: 0,
                      fontSize: '14px',
                      lineHeight: 1,
                      background: line.qty >= 99 ? '#F3F4F6' : 'white',
                      color: line.qty >= 99 ? '#D1D5DB' : '#1A1A1A',
                      border: '1px solid #E5E7EB',
                      borderRadius: '4px',
                      cursor: line.qty >= 99 ? 'not-allowed' : 'pointer',
                      fontFamily: 'inherit',
                      fontWeight: 500,
                    }}
                  >
                    +
                  </button>
                </div>

                {/* Commitment dropdown */}
                <div style={{ flexShrink: 0 }}>
                  <select
                    value={line.commitMonths}
                    onChange={(e) => updateLine(line.id, { commitMonths: Number(e.target.value) as 0 | 12 | 24 | 36 })}
                    title="Commitment length"
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '4px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      background: 'white',
                      color: '#1A1A1A',
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    <option value={0}>No commitment</option>
                    <option value={12}>12 months</option>
                    <option value={24}>24 months</option>
                    {(line.family === 'baremetal' || line.family === 'hpc_vmware') && <option value={36}>36 months</option>}
                  </select>
                </div>

                {/* Edit (pencil).
                    Custom lines: always available — this replaced an existing
                    always-visible "Edit" button, and gating it on login would
                    take away something anonymous users can already do.
                    Catalog lines: only when signed in, and only when the
                    product actually has options worth reopening the modal for. */}
                {(line.isCustomLine || (loggedIn && lineHasEditableOptions(line))) && (
                  <button
                    type="button"
                    aria-label={line.isCustomLine ? 'Edit this custom line' : 'Edit options'}
                    title={line.isCustomLine ? 'Edit this custom line' : 'Edit options'}
                    onClick={() =>
                      line.isCustomLine
                        ? handleEditCustomLine(line.id)
                        : handleEditConfiguredLine(line.id)
                    }
                    style={{
                      flexShrink: 0,
                      background: 'transparent',
                      border: 'none',
                      fontSize: '16px',
                      lineHeight: 1,
                      color: '#9CA3AF',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: '4px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#000E9C';
                      e.currentTarget.style.background = '#EEF2FF';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#9CA3AF';
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    ✎
                  </button>
                )}

                {/* Monthly price */}
                <div style={{
                  flexShrink: 0,
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#1A1A1A',
                  minWidth: '120px',
                  textAlign: 'right',
                }}>
                  ${computeLinePerUnitMonthly(line).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{line.isProfessionalServices ? ' one-time' : '/mo'}
                </div>

                {/* Remove button */}
                <button
                  type="button"
                  aria-label="Remove line"
                  onClick={() => removeLine(line.id)}
                  style={{
                    flexShrink: 0,
                    background: 'transparent',
                    border: 'none',
                    fontSize: '20px',
                    lineHeight: 1,
                    color: '#9CA3AF',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#B00020';
                    e.currentTarget.style.background = '#FEF2F2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#9CA3AF';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  ×
                </button>
                </div>

                {/* Expanded options details */}
                {line.options.length > 0 && expandedLines.has(line.id) && (
                  <div style={{
                    padding: '8px 16px 12px 16px',
                    borderTop: '1px solid #F3F4F6',
                    background: '#FAFBFC',
                    fontSize: '12px',
                    color: '#1A1A1A',
                  }}>
                    <div style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: '#6B6B6B',
                      marginBottom: '6px',
                    }}>
                      Configured options
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}>
                      {line.options.map((opt, idx) => {
                        const pct = opt.customDiscountPct ?? 0;
                        const net = optionNetPrice(opt);
                        const editing = discountEdit?.lineId === line.id && discountEdit.optionId === opt.id;
                        return (
                        <div key={`${line.id}-opt-${idx}`} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '12px',
                        }}>
                          <span style={{ color: '#1A1A1A' }}>
                            {opt.name}
                            {pct > 0 && (
                              <span style={{ marginLeft: '6px', color: '#047857', fontWeight: 600 }}>
                                −{pct}%
                              </span>
                            )}
                          </span>

                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                            {/* Custom discount is a PRESALES-only control: an
                                unauthorised discount on a quote is a commitment
                                the company never agreed to. */}
                            {isPresales && (editing ? (
                              <>
                                <input
                                  type="number" min={0} max={100} step={1} autoFocus
                                  value={discountEdit.value}
                                  onChange={(e) => setDiscountEdit({ ...discountEdit, value: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') commitDiscount();
                                    if (e.key === 'Escape') setDiscountEdit(null);
                                  }}
                                  style={{
                                    width: '62px', padding: '2px 6px', fontSize: '12px',
                                    border: '1px solid #000E9C', borderRadius: '4px',
                                    fontFamily: 'inherit', color: '#1A1A1A',
                                  }}
                                />
                                <button type="button" onClick={commitDiscount} style={discountBtn(true)}>Apply</button>
                                <button type="button" onClick={() => setDiscountEdit(null)} style={discountBtn(false)}>Cancel</button>
                              </>
                            ) : (
                              <button
                                type="button"
                                title="Set a custom discount for this option"
                                onClick={() => setDiscountEdit({ lineId: line.id, optionId: opt.id, value: String(pct || '') })}
                                style={discountBtn(false)}
                              >
                                {pct > 0 ? 'Edit discount' : 'Discount'}
                              </button>
                            ))}

                            {pct > 0 ? (
                              <span style={{ fontWeight: 500 }}>
                                <s style={{ color: '#9CA3AF', fontWeight: 400, marginRight: '6px' }}>
                                  ${opt.monthlyPrice.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </s>
                                <span style={{ color: '#047857' }}>
                                  +${net.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
                                </span>
                              </span>
                            ) : (
                              <span style={{ color: '#1A1A1A', fontWeight: 500 }}>
                                +${opt.monthlyPrice.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
                              </span>
                            )}
                          </span>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={{
        marginBottom: '20px',
        padding: '24px',
        background: 'white',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: '#E6F1FB',
            color: '#000E9C',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 600,
          }}>4</div>
          <h2 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 600,
            color: '#1A1A1A',
          }}>
            Comments
          </h2>
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '11px',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: '#6B6B6B',
            marginBottom: '6px',
          }}>
            Notes for the customer <span style={{ color: '#9CA3AF', textTransform: 'none' }}>(optional)</span>
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder={language === 'fr'
              ? 'Ex. Devis valide jusqu\'au 30 juin · Inclut le support de migration · Conditions de paiement Net 30'
              : 'e.g. Quote valid until June 30 · Includes white-glove migration support · Net-30 payment terms'}
            rows={4}
            style={{
              width: '100%',
              padding: '10px 12px',
              fontSize: '14px',
              fontFamily: 'inherit',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              lineHeight: 1.5,
            }}
          />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginTop: '6px',
          }}>
            <p style={{
              margin: 0,
              fontSize: '11px',
              color: '#6B6B6B',
              fontStyle: 'italic',
            }}>
              {language === 'fr'
                ? 'Apparaît sur le PDF, sous les totaux.'
                : 'Appears on the PDF, below the totals.'}
            </p>
            <p style={{
              margin: 0,
              fontSize: '11px',
              color: comments.length > 500 ? '#B00020' : '#9CA3AF',
              fontWeight: comments.length > 500 ? 500 : 400,
            }}>
              {comments.length} / 500
            </p>
          </div>
        </div>
      </section>
      </div>

      <aside style={{
        position: 'sticky',
        top: '24px',
        alignSelf: 'start',
        minWidth: 0,
      }}>
        <div style={{
          background: 'white',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <h2 style={{
            margin: '0 0 16px 0',
            fontSize: '16px',
            fontWeight: 600,
            color: '#1A1A1A',
          }}>
            Quote summary
          </h2>
          {lines.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            {/* Subtotal row */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'flex-start',
              fontSize: '13px',
              color: '#9CA3AF',
            }}>
              <span>Monthly subtotal</span>
              <span style={{ fontStyle: 'italic', whiteSpace: 'nowrap' }}>—</span>
            </div>

            {/* Support row */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'flex-start',
              fontSize: '13px',
              color: '#9CA3AF',
            }}>
              <span>Support surcharge</span>
              <span style={{ fontStyle: 'italic', whiteSpace: 'nowrap' }}>—</span>
            </div>

            {/* Tax rows (province-aware) */}
            {getProvinceTaxesForDisplay().map((tax) => (
              <div key={tax.name} style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                alignItems: 'flex-start',
                fontSize: '13px',
                color: '#9CA3AF',
              }}>
                <span>{tax.name} {(tax.rate * 100).toLocaleString('en-CA', { maximumFractionDigits: 3 })}%</span>
                <span style={{ fontStyle: 'italic', whiteSpace: 'nowrap' }}>—</span>
              </div>
            ))}

            {/* Total row — emphasized with top border */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'flex-start',
              fontSize: '14px',
              fontWeight: 600,
              color: '#9CA3AF',
              borderTop: '1px solid #E5E7EB',
              paddingTop: '10px',
              marginTop: '6px',
            }}>
              <span>Monthly total</span>
              <span style={{ fontStyle: 'italic', whiteSpace: 'nowrap' }}>—</span>
            </div>

            {/* Footer hint */}
            <p style={{
              margin: '8px 0 0 0',
              fontSize: '11px',
              color: '#9CA3AF',
              fontStyle: 'italic',
              textAlign: 'center',
            }}>
              Add products to see totals.
            </p>
          </div>
          ) : (
          // LIVE TOTALS
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}>
            {/* List price */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'flex-start',
              fontSize: '13px',
              color: '#1A1A1A',
            }}>
              <span style={{ color: '#6B6B6B' }}>List price</span>
              <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                ${totalsAudit.listPrice.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>

            {/* Commitment discount — one row per commitment term */}
            {totalsAudit.commitmentRows.map((row) => (
              <div
                key={`commit-${row.months}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  alignItems: 'flex-start',
                  fontSize: '12px',
                  color: '#047857',
                }}
              >
                <span>
                  Commitment discount ({row.months} months · {row.rateLabel})
                </span>
                <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                  − ${row.amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            ))}

            {/* Partner discount (only if > 0) */}
            {totalsAudit.partnerSavings > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                alignItems: 'flex-start',
                fontSize: '12px',
                color: '#047857',
              }}>
                <span>
                  Partner discount
                  {partnerTier === 'standard' && ' (Standard: 5% deal reg)'}
                  {partnerTier === 'advanced' && ' (Advanced: 5% + 5%)'}
                  {partnerTier === 'elite' && ' (Elite: 5% + 10%)'}
                </span>
                <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                  − ${totalsAudit.partnerSavings.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* Monthly subtotal (after both discounts) */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'flex-start',
              fontSize: '13px',
              color: '#1A1A1A',
              borderTop: '1px solid #F3F4F6',
              paddingTop: '6px',
              marginTop: '4px',
            }}>
              <span style={{ color: '#6B6B6B' }}>Monthly subtotal</span>
              <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                ${totals.subtotalMonthly.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>

            {/* Special (exceptional) discount — shown only when set */}
            {totals.specialDiscountAmount > 0 && (
              <>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  alignItems: 'flex-start',
                  fontSize: '13px',
                  color: '#0F7B3F',
                }}>
                  <span>Special discount (−{totals.specialDiscountPct}%)</span>
                  <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                    −${totals.specialDiscountAmount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '12px',
                  alignItems: 'flex-start',
                  fontSize: '13px',
                  color: '#1A1A1A',
                }}>
                  <span style={{ color: '#6B6B6B' }}>Subtotal after discount</span>
                  <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                    ${totals.subtotalAfterSpecialDiscount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </>
            )}

            {/* Support surcharge */}
            {totals.supportSurcharge > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                alignItems: 'flex-start',
                fontSize: '13px',
                color: '#1A1A1A',
              }}>
                <span style={{ color: '#6B6B6B' }}>
                  Support surcharge
                  {supportTier === 'business' && ' (Business +10%)'}
                  {supportTier === 'business_baremetal' && ' (Business BareMetal +5%)'}
                </span>
                <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                  ${totals.supportSurcharge.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* Monthly before tax */}
            {totals.taxes.total > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                alignItems: 'flex-start',
                fontSize: '13px',
                color: '#1A1A1A',
                borderTop: '1px solid #F3F4F6',
                paddingTop: '6px',
                marginTop: '4px',
              }}>
                <span style={{ color: '#6B6B6B' }}>Monthly before tax</span>
                <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                  ${totals.monthlyWithSupport.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* Tax rows — only render non-zero taxes */}
            {totals.taxes.gst > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                alignItems: 'flex-start',
                fontSize: '12px',
                color: '#1A1A1A',
              }}>
                <span style={{ color: '#6B6B6B' }}>GST 5%</span>
                <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                  ${totals.taxes.gst.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
            {totals.taxes.hst > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                alignItems: 'flex-start',
                fontSize: '12px',
                color: '#1A1A1A',
              }}>
                <span style={{ color: '#6B6B6B' }}>HST</span>
                <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                  ${totals.taxes.hst.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
            {totals.taxes.pstQst > 0 && totals.taxes.pstQstLabel && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                alignItems: 'flex-start',
                fontSize: '12px',
                color: '#1A1A1A',
              }}>
                <span style={{ color: '#6B6B6B' }}>{totals.taxes.pstQstLabel}</span>
                <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                  ${totals.taxes.pstQst.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* Tax-exempt note (if applicable) */}
            {(client.taxExempt || market === 'WORLD') && totals.taxes.total === 0 && (
              <div style={{
                fontSize: '11px',
                color: '#6B6B6B',
                fontStyle: 'italic',
              }}>
                {market === 'WORLD' ? 'Tax exempt (international)' : 'Tax-exempt — no taxes applied'}
              </div>
            )}

            {/* Monthly total — emphasized */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              alignItems: 'flex-start',
              fontSize: '15px',
              fontWeight: 600,
              color: '#1A1A1A',
              borderTop: '2px solid #1A1A1A',
              paddingTop: '10px',
              marginTop: '6px',
            }}>
              <span>Monthly total <span style={{ fontWeight: 400, color: '#6B6B6B', fontSize: '12px' }}>({market === 'WORLD' ? 'USD' : 'CAD'})</span></span>
              <span style={{ color: '#000E9C', whiteSpace: 'nowrap' }}>
                ${totals.monthlyAllIn.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>

            {/* One-time setup fees */}
            {totals.setupFeesTotal > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                alignItems: 'flex-start',
                fontSize: '12px',
                color: '#6B6B6B',
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px dashed #E5E7EB',
              }}>
                <span>One-time setup fees</span>
                <span style={{ fontWeight: 500, color: '#1A1A1A', whiteSpace: 'nowrap' }}>
                  ${totals.setupFeesTotal.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* One-time professional services (excluded from the monthly) */}
            {totals.professionalServicesTotal > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                alignItems: 'flex-start',
                fontSize: '12px',
                color: '#6B6B6B',
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px dashed #E5E7EB',
              }}>
                <span>One-time professional services</span>
                <span style={{ fontWeight: 500, color: '#1A1A1A', whiteSpace: 'nowrap' }}>
                  ${totals.professionalServicesTotal.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
          )}

          {/* PDF actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
            <button
              type="button"
              onClick={handlePreview}
              disabled={lines.length === 0 || pdfLoading}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 500,
                background: 'white',
                color: '#1A1A1A',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                ...(lines.length === 0 || pdfLoading
                  ? { background: '#9CA3AF', color: 'white', border: 'none', cursor: 'not-allowed' }
                  : {}),
              }}
            >
              {pdfLoading ? 'Generating…' : 'Preview'}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={lines.length === 0 || pdfLoading}
              style={{
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 500,
                background: lines.length === 0 || pdfLoading ? '#9CA3AF' : '#000E9C',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: lines.length === 0 || pdfLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {pdfLoading ? 'Generating…' : 'Download PDF'}
            </button>
          </div>
          {pdfError && (
            <div style={{ color: '#DC2626', fontSize: '13px', marginTop: '8px', textAlign: 'right' }}>
              {pdfError}
            </div>
          )}
          {openDealId && (
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px', textAlign: 'right' }}>
              Editing deal <strong style={{ fontFamily: 'monospace' }}>{openDealId}</strong>
              {' · '}
              <button
                type="button"
                onClick={() => { setOpenDealId(null); setRepriceNotice(null); }}
                title="Stop reusing this quote number; the next PDF gets a new one"
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#000E9C', cursor: 'pointer', font: 'inherit', textDecoration: 'underline',
                }}
              >
                detach
              </button>
            </div>
          )}
          {repriceNotice && (
            <div style={{
              fontSize: '12px', color: '#92400E', background: '#FFFBEB',
              border: '1px solid #FDE68A', borderRadius: '6px',
              padding: '8px 10px', marginTop: '8px', textAlign: 'left',
            }}>
              {repriceNotice}
            </div>
          )}
        </div>
      </aside>

      {usersOpen && session.user && (
        <ManageUsers currentUser={session.user} onClose={() => { setUsersOpen(false); session.refresh(); }} />
      )}

      {dealsOpen && (
        <ExistingDeals onClose={() => setDealsOpen(false)} onOpen={handleOpenDeal} />
      )}

      {customOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          zIndex: 100,
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '600px',
            maxHeight: 'calc(100vh - 40px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div>
                <h2 style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#1A1A1A',
                }}>
                  {customEditingLineId ? 'Edit custom line' : 'Add custom line'}
                </h2>
                <p style={{
                  margin: '4px 0 0 0',
                  fontSize: '12px',
                  color: '#6B6B6B',
                }}>
                  For products or services not in the OVH catalog.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCustomOpen(false)}
                aria-label="Close"
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  lineHeight: 1,
                  color: '#9CA3AF',
                  cursor: 'pointer',
                  padding: '4px 8px',
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{
              padding: '20px 24px',
              overflow: 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
              {/* Name */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: '#6B6B6B',
                  marginBottom: '6px',
                }}>
                  Name <span style={{ color: '#B00020' }}>*</span>
                </label>
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  placeholder="e.g. VMware vCenter license"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    outline: 'none',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Description */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: '#6B6B6B',
                  marginBottom: '6px',
                }}>
                  Description <span style={{ color: '#9CA3AF', textTransform: 'none' }}>(optional)</span>
                </label>
                <input
                  type="text"
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  placeholder="e.g. Standard edition, 50 vCPU pack"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '14px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    outline: 'none',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Price row: monthly + setup side-by-side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '11px',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: '#6B6B6B',
                    marginBottom: '6px',
                  }}>
                    Monthly price ({market === 'WORLD' ? 'USD' : 'CAD'}) <span style={{ color: '#B00020' }}>*</span>
                  </label>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={customMonthlyPrice}
                    onChange={(e) => setCustomMonthlyPrice(e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '11px',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: '#6B6B6B',
                    marginBottom: '6px',
                  }}>
                    Setup fee <span style={{ color: '#9CA3AF', textTransform: 'none' }}>(optional)</span>
                  </label>
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={customSetupFee}
                    onChange={(e) => setCustomSetupFee(e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              {/* DC + Commitment row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '11px',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: '#6B6B6B',
                    marginBottom: '6px',
                  }}>
                    Datacenter
                  </label>
                  <select
                    value={customDc}
                    onChange={(e) => setCustomDc(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      background: 'white',
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                    }}
                  >
                    {PICKER_DC_OPTIONS.map((dc) => (
                      <option key={dc.value} value={dc.value}>{dc.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '11px',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: '#6B6B6B',
                    marginBottom: '6px',
                  }}>
                    Commitment
                  </label>
                  <select
                    value={customCommitMonths}
                    onChange={(e) => setCustomCommitMonths(Number(e.target.value) as 0 | 12 | 24 | 36)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '14px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      outline: 'none',
                      fontFamily: 'inherit',
                      background: 'white',
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                    }}
                  >
                    <option value={0}>No commitment</option>
                    <option value={12}>12 months</option>
                    <option value={24}>24 months</option>
                    <option value={36}>36 months</option>
                  </select>
                  <p style={{
                    margin: '4px 0 0 0',
                    fontSize: '10px',
                    color: '#9CA3AF',
                    fontStyle: 'italic',
                  }}>
                    Label only — no discount applied
                  </p>
                </div>
              </div>

              {/* Partner discount toggle */}
              <div style={{
                padding: '12px 14px',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={customApplyPartnerDiscount}
                    onChange={(e) => setCustomApplyPartnerDiscount(e.target.checked)}
                    style={{
                      marginTop: '2px',
                      cursor: 'pointer',
                    }}
                  />
                  <span>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>
                      Apply partner discount
                    </span>
                    <span style={{ display: 'block', fontSize: '11px', color: '#6B6B6B', marginTop: '2px' }}>
                      If checked, the current partner tier discount applies to this line.
                    </span>
                  </span>
                </label>
              </div>

              {/* Professional Services toggle */}
              <div style={{
                padding: '12px 14px',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                marginTop: '12px',
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={customIsProfessionalServices}
                    onChange={(e) => setCustomIsProfessionalServices(e.target.checked)}
                    style={{ marginTop: '2px', cursor: 'pointer' }}
                  />
                  <span>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: '#1A1A1A' }}>
                      These are Professional Services
                    </span>
                    <span style={{ display: 'block', fontSize: '11px', color: '#6B6B6B', marginTop: '2px' }}>
                      Billed as a <strong>one-time fee</strong> (not monthly): the price above shows as
                      SERVICES PROFESSIONNELS HT / (INCLUANT TAXES) at the bottom of the quote, and adds
                      the Professional Services terms &amp; conditions to the PDF.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              background: '#FAFBFC',
            }}>
              <button
                type="button"
                onClick={() => { setCustomEditingLineId(null); setCustomOpen(false); }}
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 500,
                  background: 'white',
                  color: '#1A1A1A',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitCustomLine}
                disabled={
                  !customName.trim() ||
                  parseFloat(customMonthlyPrice) <= 0 ||
                  isNaN(parseFloat(customMonthlyPrice))
                }
                style={{
                  padding: '10px 20px',
                  fontSize: '14px',
                  fontWeight: 500,
                  background: (!customName.trim() || parseFloat(customMonthlyPrice) <= 0 || isNaN(parseFloat(customMonthlyPrice))) ? '#9CA3AF' : '#000E9C',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (!customName.trim() || parseFloat(customMonthlyPrice) <= 0 || isNaN(parseFloat(customMonthlyPrice))) ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {customEditingLineId ? 'Save changes' : 'Add to quote'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pickerOpen && (
        <div
          onClick={() => setPickerOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '900px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '20px 24px',
              borderBottom: '1px solid #E5E7EB',
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '17px',
                fontWeight: 600,
                color: '#1A1A1A',
              }}>
                Add a product
              </h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setPickerOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '22px',
                  lineHeight: 1,
                  color: '#6B6B6B',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '4px',
                }}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flex: 1,
            }}>
            {configProduct ? (
              // ----- CONFIGURATOR VIEW -----
              <>
                {/* Configurator header — sticky */}
                <div style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid #E5E7EB',
                  background: '#FAFBFC',
                  flexShrink: 0,
                }}>
                  <button
                    type="button"
                    onClick={() => setConfigProduct(null)}
                    style={{
                      padding: '4px 0',
                      fontSize: '13px',
                      background: 'transparent',
                      border: 'none',
                      color: '#000E9C',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      marginBottom: '8px',
                    }}
                  >
                    ← Back to products
                  </button>
                  <div style={{
                    fontSize: '15px',
                    fontWeight: 600,
                    color: '#1A1A1A',
                  }}>
                    Configure: {configProduct.name}
                    {renderYearChip(configProduct.releaseYear)}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: '#6B6B6B',
                    marginTop: '2px',
                  }}>
                    Base price: ${configProduct.monthlyPriceCad.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
                  </div>
                </div>

                {/* Configurator body — scrollable */}
                <div style={{
                  flex: 1,
                  overflow: 'auto',
                  padding: '20px 24px',
                }}>
                  {renderOptionDropdown('RAM', 'ram', selectedRam, setSelectedRam)}
                  {/* Included SYSTEM disk — fixed, shown read-only (not part of the
                      data-storage dropdown below). Only servers that carry a separate
                      system disk render this. */}
                  {(() => {
                    if (!configProduct) return null;
                    const sysDisks = configProduct.options
                      .filter((o) => o.family === SYSTEM_DISK_FAMILY)
                      .sort((a, b) => a.monthlyPriceCad - b.monthlyPriceCad);
                    const sys = sysDisks[0];
                    if (!sys) return null;
                    return (
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{
                          display: 'block', fontSize: '11px', fontWeight: 500,
                          textTransform: 'uppercase', letterSpacing: '0.04em',
                          color: '#6B6B6B', marginBottom: '6px',
                        }}>
                          System Disk
                        </label>
                        <div style={{
                          width: '100%', padding: '8px 12px', fontSize: '14px',
                          border: '1px solid #E5E7EB', borderRadius: '6px',
                          background: '#F3F4F6', color: '#6B6B6B', boxSizing: 'border-box',
                        }}>
                          {sys.name} — Included
                        </div>
                      </div>
                    );
                  })()}
                  {renderOptionDropdown(
                    configProduct?.options.some((o) => o.family === SYSTEM_DISK_FAMILY) ? 'Data storage' : 'Storage',
                    'disk',
                    selectedDisk,
                    setSelectedDisk,
                    (o) => o.family !== SYSTEM_DISK_FAMILY,
                  )}
                  {renderOptionDropdown('Public bandwidth', 'bandwidth', selectedBandwidth, setSelectedBandwidth)}
                </div>

                {/* Configurator footer — live total + Add to quote */}
                <div style={{
                  padding: '16px 24px',
                  borderTop: '1px solid #E5E7EB',
                  background: '#FAFBFC',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}>
                  <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6B6B6B', marginBottom: '4px' }}>
                      Configured monthly price
                    </div>
                    <div style={{ fontSize: '20px', fontWeight: 600, color: '#1A1A1A' }}>
                      ${configLiveTotal.total.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
                    </div>
                    <div style={{ fontSize: '11px', color: '#6B6B6B', marginTop: '2px' }}>
                      Base ${configLiveTotal.base.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {configLiveTotal.ramDelta > 0 && ' + RAM $' + configLiveTotal.ramDelta.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {configLiveTotal.diskDelta > 0 && ' + Storage $' + configLiveTotal.diskDelta.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {configLiveTotal.bwDelta > 0 && ' + Bandwidth $' + configLiveTotal.bwDelta.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={configEditingLineId ? handleSaveConfiguredLine : handleAddConfiguredProduct}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: 500,
                      background: '#000E9C',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      flexShrink: 0,
                    }}
                  >
                    {configEditingLineId ? 'Save changes' : 'Add to quote'}
                  </button>
                </div>
              </>
            ) : (
              // ----- PRODUCT LIST VIEW (existing — wrapped) -----
              <>
              {/* Filter controls — sticky at the top */}
              <div style={{
                padding: '16px 24px',
                borderBottom: '1px solid #E5E7EB',
                background: '#FAFBFC',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                flexShrink: 0,
              }}>
                {/* Row 1: DC dropdown + Search */}
                <div style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}>
                  <div style={{ flex: '1 1 280px', minWidth: '280px' }}>
                    <label htmlFor="pickerDc" style={{
                      display: 'block',
                      fontSize: '11px',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: '#6B6B6B',
                      marginBottom: '4px',
                    }}>
                      Datacenter <span style={{ color: '#B00020', fontWeight: 600 }}>*</span>
                    </label>
                    <select
                      id="pickerDc"
                      value={pickerDc}
                      onChange={(e) => setPickerDc(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: '14px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px',
                        outline: 'none',
                        fontFamily: 'inherit',
                        background: 'white',
                        color: '#1A1A1A',
                        boxSizing: 'border-box',
                        cursor: 'pointer',
                        fontWeight: 500,
                      }}
                    >
                      <option value="">— Select a datacenter —</option>
                      {PICKER_DC_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ flex: '1 1 260px', minWidth: '260px' }}>
                    <label htmlFor="pickerSearch" style={{
                      display: 'block',
                      fontSize: '11px',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color: '#6B6B6B',
                      marginBottom: '4px',
                    }}>
                      Search
                    </label>
                    <input
                      id="pickerSearch"
                      type="text"
                      value={pickerSearch}
                      onChange={(e) => setPickerSearch(e.target.value)}
                      placeholder="e.g. ADVANCE-1, GP-3, datastore"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        fontSize: '14px',
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px',
                        outline: 'none',
                        fontFamily: 'inherit',
                        background: 'white',
                        color: '#1A1A1A',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                {/* Row 2: Family chips */}
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    color: '#6B6B6B',
                    marginRight: '4px',
                  }}>
                    Family
                  </span>
                  {PICKER_FAMILY_OPTIONS.map((fam) => {
                    const isActive = pickerFamilies.has(fam.value);
                    return (
                      <button
                        key={fam.value}
                        type="button"
                        onClick={() => {
                          setPickerFamilies((prev) => {
                            const next = new Set(prev);
                            if (next.has(fam.value)) {
                              if (next.size > 1) next.delete(fam.value); // can't deselect all
                            } else {
                              next.add(fam.value);
                            }
                            return next;
                          });
                        }}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          border: '1px solid',
                          borderColor: isActive ? '#000E9C' : '#E5E7EB',
                          background: isActive ? '#000E9C' : 'white',
                          color: isActive ? 'white' : '#6B6B6B',
                          borderRadius: '999px',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        {fam.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Scrollable product list */}
              <div style={{
                flex: 1,
                overflow: 'auto',
                padding: '16px 24px',
              }}>
                {catalogLoading && (
                  <p style={{ margin: 0, fontSize: '14px', color: '#6B6B6B' }}>Loading catalog…</p>
                )}

                {catalogError && (
                  <p style={{ margin: 0, fontSize: '14px', color: '#B00020' }}>
                    Failed to load catalog: {catalogError}
                  </p>
                )}

                {!catalogLoading && !catalogError && catalog && !pickerDc && (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#6B6B6B',
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>📍</div>
                    <div style={{ fontSize: '15px', fontWeight: 500, color: '#1A1A1A', marginBottom: '6px' }}>
                      Choose a datacenter to see available products
                    </div>
                    <div style={{ fontSize: '13px' }}>
                      Products differ by location — pick where the customer wants to deploy.
                    </div>
                  </div>
                )}

                {!catalogLoading && !catalogError && catalog && pickerDc && filteredProducts.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: '#6B6B6B',
                    fontSize: '14px',
                  }}>
                    No products match your filters in this datacenter.
                  </div>
                )}

                {!catalogLoading && !catalogError && catalog && pickerDc && filteredProducts.length > 0 && (
                  <>
                    <p style={{
                      margin: '0 0 12px 0',
                      fontSize: '12px',
                      color: '#6B6B6B',
                    }}>
                      {filteredProducts.length} product{filteredProducts.length === 1 ? '' : 's'} available
                    </p>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}>
                      {filteredProducts.map((product) => (
                        <div
                          key={product.id}
                          style={{
                            padding: '12px 16px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '12px',
                            background: 'white',
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: 500,
                              color: '#1A1A1A',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {product.name}
                              {renderYearChip(product.releaseYear)}
                            </div>
                            <div style={{
                              fontSize: '11px',
                              color: '#6B6B6B',
                              marginTop: '2px',
                            }}>
                              {product.family === 'baremetal' ? 'BareMetal' : 'HPC VMware'} · {product.range}
                            </div>
                          </div>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#1A1A1A',
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                          }}>
                            ${product.monthlyPriceCad.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo
                          </div>
                          {product.family === 'baremetal' ? (
                            <div style={{
                              flexShrink: 0,
                              display: 'flex',
                              gap: '6px',
                            }}>
                              <button
                                type="button"
                                onClick={() => handleConfigureProduct(product)}
                                style={{
                                  padding: '6px 12px',
                                  fontSize: '13px',
                                  fontWeight: 500,
                                  background: 'white',
                                  color: '#000E9C',
                                  border: '1px solid #000E9C',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontFamily: 'inherit',
                                }}
                              >
                                Configure
                              </button>
                              <button
                                type="button"
                                onClick={() => handleAddProduct(product)}
                                style={{
                                  padding: '6px 14px',
                                  fontSize: '13px',
                                  fontWeight: 500,
                                  background: '#000E9C',
                                  color: 'white',
                                  border: '1px solid #000E9C',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontFamily: 'inherit',
                                }}
                              >
                                Add as-is
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleAddProduct(product)}
                              style={{
                                padding: '6px 14px',
                                fontSize: '13px',
                                fontWeight: 500,
                                background: '#000E9C',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                flexShrink: 0,
                              }}
                            >
                              Add
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              </>
            )}
            </div>
          </div>
        </div>
      )}
    </main>
    </>
  );
}
